'use client';

/**
 * EntityActivityTimeline Component
 *
 * A polished timeline component that displays entity activity with:
 * - Time-of-day grouping (Morning, Noon, Afternoon, Evening)
 * - Visual threading connector between events
 * - Collapsible time periods
 * - Color-coded action types
 *
 * @see Issue #234 - Entity Audit Activity Logs
 */

import AddIcon from '@mui/icons-material/Add';
import AssignIcon from '@mui/icons-material/Assignment';
import AfternoonIcon from '@mui/icons-material/Brightness4';
import MorningIcon from '@mui/icons-material/Brightness5';
import NoonIcon from '@mui/icons-material/Brightness6';
import ApproveIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/CloudDownload';
import UploadIcon from '@mui/icons-material/CloudUpload';
import CommentIcon from '@mui/icons-material/Comment';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import EveningIcon from '@mui/icons-material/NightsStay';
import SendIcon from '@mui/icons-material/Send';
import StatusChangeIcon from '@mui/icons-material/SwapHoriz';
import ViewIcon from '@mui/icons-material/Visibility';
import { Box, Chip, CircularProgress, Collapse, IconButton, Skeleton, Typography } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import type { NeonAccent } from '../../theme/palette';
import { baseColors, eventTypeColors, getNeonStyles } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import { formatCompactRelativeTime, formatFullDateTime } from '../../utils/formatters';
import { transientOptions } from '../../utils/styledUtils';
import { GlassCard } from '../GlassCard/GlassCard';
import { GlassTooltip } from '../GlassTooltip';
import { UserAvatar } from '../UserAvatar/UserAvatar';

// ============================================================================
// Types
// ============================================================================

export interface ActivityEvent {
  id: string;
  type: string;
  action: string;
  /** Optional human-readable action label (e.g. "Document Uploaded") */
  actionName?: string;
  description: string;
  createdAt: string;
  actorId?: string;
  actorName?: string;
  actorEmail?: string;
  actorRole?: string;
  actorAvatar?: string;
  /** Severity — the four values are the canonical palette, but any string is
   * accepted so backend payloads that carry raw enum names (or null) don't
   * need to be narrowed at every consumer. */
  severity?: 'info' | 'warning' | 'error' | 'critical' | string | null;
  status?: 'success' | 'failure' | 'pending' | string | null;
  /** Forensic context — surfaced in the click-to-expand details panel. */
  ipAddress?: string | null;
  userAgent?: string | null;
  /** HTTP method that produced this audit event, when applicable */
  method?: string | null;
  /** HTTP path that produced this audit event, when applicable */
  path?: string | null;
  /** Request/response duration in ms, when audit-service captured it */
  duration?: number | null;
  /** Tags propagated from the audit event */
  tags?: string[];
  /** Error message attached to a failed audit event */
  errorMessage?: string | null;
  /** ID of the resource the event acted on */
  resourceId?: string;
  metadata?: Record<string, unknown>;
  resourceLink?: string;
}

export type TimeOfDay = 'morning' | 'noon' | 'afternoon' | 'evening';

export interface GroupedTimelineData {
  dateKey: string;
  dateLabel: string;
  periods: {
    period: TimeOfDay;
    label: string;
    events: ActivityEvent[];
    timeRange: string;
  }[];
}

export interface EntityActivityTimelineProps {
  /** Events to display */
  events: ActivityEvent[];
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string;
  /** Maximum items to show */
  maxItems?: number;
  /** Enable time-of-day grouping */
  groupByTimeOfDay?: boolean;
  /** Show threading connector line */
  showThreading?: boolean;
  /** Load more handler */
  onLoadMore?: () => void;
  /** Has more items to load */
  hasMore?: boolean;
  /** Event click handler */
  onEventClick?: (event: ActivityEvent) => void;
  /** Show user avatars */
  showAvatars?: boolean;
  /** Show relative time (e.g., "2h ago") */
  relativeTime?: boolean;
  /** Appearance variant */
  variant?: 'glass' | 'minimal';
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Empty state message */
  emptyMessage?: string;
  /** Title */
  title?: string;
  /** Show card wrapper */
  showCard?: boolean;
  /** Default collapsed state for periods */
  defaultCollapsed?: boolean;
  /**
   * When true, clicking a row toggles an inline detail panel showing the
   * full audit context (actor + role, full timestamp, IP, parsed user-agent,
   * method/path, severity, tags, duration, error). When `onEventClick` is
   * also provided it still fires alongside the expand toggle.
   * @default true
   */
  expandableRows?: boolean;
  /**
   * Override the rendered resource label in event descriptions. The audit
   * service stores the canonical resource type (e.g. "case"); the loan
   * detail page sets this to "loan" so descriptions read "viewed this loan"
   * instead of "viewed this case". Substituted only on the user-facing
   * description, never on the raw event data.
   */
  resourceLabelOverride?: string;
}

// ============================================================================
// Constants
// ============================================================================

const TIME_PERIOD_KEYS: Record<
  TimeOfDay,
  { labelKey: string; rangeKey: string; icon: React.ElementType; color: string }
> = {
  morning: {
    labelKey: 'timeline.period.morning',
    rangeKey: 'timeline.period.morningRange',
    icon: MorningIcon,
    color: baseColors.warning[300],
  },
  noon: {
    labelKey: 'timeline.period.noon',
    rangeKey: 'timeline.period.noonRange',
    icon: NoonIcon,
    color: baseColors.error[300],
  },
  afternoon: {
    labelKey: 'timeline.period.afternoon',
    rangeKey: 'timeline.period.afternoonRange',
    icon: AfternoonIcon,
    // TODO(#1758 F2): kept as blue (time-of-day category, not nav/selection) — clashes with `automated`/teal if migrated.
    color: baseColors.info[400],
  },
  evening: {
    labelKey: 'timeline.period.evening',
    rangeKey: 'timeline.period.eveningRange',
    icon: EveningIcon,
    color: baseColors.primary[400],
  },
};

const ACTION_ICONS: Record<string, React.ElementType> = {
  create: AddIcon,
  created: AddIcon,
  add: AddIcon,
  added: AddIcon,
  update: EditIcon,
  updated: EditIcon,
  edit: EditIcon,
  edited: EditIcon,
  delete: DeleteIcon,
  deleted: DeleteIcon,
  remove: DeleteIcon,
  removed: DeleteIcon,
  assign: AssignIcon,
  assigned: AssignIcon,
  approve: ApproveIcon,
  approved: ApproveIcon,
  reject: ErrorIcon,
  rejected: ErrorIcon,
  comment: CommentIcon,
  commented: CommentIcon,
  note: CommentIcon,
  email: EmailIcon,
  sent: SendIcon,
  send: SendIcon,
  invite: SendIcon,
  invited: SendIcon,
  upload: UploadIcon,
  uploaded: UploadIcon,
  download: DownloadIcon,
  downloaded: DownloadIcon,
  status_change: StatusChangeIcon,
  status_changed: StatusChangeIcon,
  login: LoginIcon,
  logout: LogoutIcon,
  view: ViewIcon,
  viewed: ViewIcon,
  read: ViewIcon,
};

const ACTION_COLORS: Record<string, string> = {
  create: eventTypeColors.created,
  created: eventTypeColors.created,
  add: eventTypeColors.created,
  added: eventTypeColors.created,
  approve: eventTypeColors.created,
  approved: eventTypeColors.created,
  resolve: eventTypeColors.created,
  resolved: eventTypeColors.created,
  update: eventTypeColors.updated,
  updated: eventTypeColors.updated,
  edit: eventTypeColors.updated,
  edited: eventTypeColors.updated,
  assign: eventTypeColors.assignment,
  assigned: eventTypeColors.assignment,
  login: eventTypeColors.updated,
  comment: eventTypeColors.comment,
  commented: eventTypeColors.comment,
  note: eventTypeColors.comment,
  delete: eventTypeColors.deleted,
  deleted: eventTypeColors.deleted,
  remove: eventTypeColors.deleted,
  removed: eventTypeColors.deleted,
  reject: eventTypeColors.deleted,
  rejected: eventTypeColors.deleted,
  email: baseColors.secondary[500],
  sent: baseColors.secondary[500],
  send: baseColors.secondary[500],
  invite: baseColors.secondary[500],
  invited: baseColors.secondary[500],
  upload: baseColors.warning[500],
  uploaded: baseColors.warning[500],
  download: baseColors.warning[500],
  downloaded: baseColors.warning[500],
};

// ============================================================================
// Helper Functions
// ============================================================================

function getTimeOfDay(timestamp: string): TimeOfDay {
  const hour = new Date(timestamp).getHours();
  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 14) return 'noon';
  if (hour >= 14 && hour < 18) return 'afternoon';
  return 'evening';
}

function getActionIcon(action: string | undefined | null): React.ElementType {
  if (!action) return InfoIcon;
  const normalizedAction = action.toLowerCase().replace(/[^a-z_]/g, '_');
  return ACTION_ICONS[normalizedAction] ?? InfoIcon;
}

function getActionColor(action: string | undefined | null): string {
  if (!action) return baseColors.neutral[500];
  const normalizedAction = action.toLowerCase().replace(/[^a-z_]/g, '_');
  return ACTION_COLORS[normalizedAction] ?? baseColors.neutral[500];
}

function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDateLabel(dateKey: string, t: (key: string) => string): string {
  const date = new Date(dateKey);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (dateKey === today.toISOString().split('T')[0]) {
    return t('common.today');
  }
  if (dateKey === yesterday.toISOString().split('T')[0]) {
    return t('common.yesterday');
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function groupEventsByDateAndTime(events: ActivityEvent[], t: (key: string) => string): GroupedTimelineData[] {
  const dateGroups = new Map<string, Map<TimeOfDay, ActivityEvent[]>>();

  // Filter out events with invalid dates and sort by date descending
  const validEvents = events.filter((event) => {
    const date = new Date(event.createdAt);
    return !Number.isNaN(date.getTime());
  });
  const sortedEvents = [...validEvents].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  for (const event of sortedEvents) {
    const eventDate = new Date(event.createdAt);
    const dateKey = eventDate.toISOString().split('T')[0];
    const timeOfDay = getTimeOfDay(event.createdAt);

    if (!dateGroups.has(dateKey)) {
      dateGroups.set(dateKey, new Map());
    }

    const dayGroup = dateGroups.get(dateKey)!;
    if (!dayGroup.has(timeOfDay)) {
      dayGroup.set(timeOfDay, []);
    }
    dayGroup.get(timeOfDay)!.push(event);
  }

  const result: GroupedTimelineData[] = [];

  for (const [dateKey, periods] of dateGroups) {
    const periodData: GroupedTimelineData['periods'] = [];

    // Order periods: morning, noon, afternoon, evening (but reversed for display)
    const periodOrder: TimeOfDay[] = ['evening', 'afternoon', 'noon', 'morning'];

    for (const period of periodOrder) {
      if (periods.has(period)) {
        const periodInfo = TIME_PERIOD_KEYS[period];
        periodData.push({
          period,
          label: periodInfo.labelKey,
          events: periods.get(period)!,
          timeRange: periodInfo.rangeKey,
        });
      }
    }

    result.push({
      dateKey,
      dateLabel: formatDateLabel(dateKey, t),
      periods: periodData,
    });
  }

  return result;
}

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled(Box)({
  width: '100%',
});

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

const DateDivider = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  margin: theme.spacing(2, 0),
  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: 1,
    background: theme.palette.divider,
  },
}));

interface PeriodHeaderProps {
  $color: string;
  $isDark: boolean;
}

const PeriodHeader = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<PeriodHeaderProps>(({ theme, $color, $isDark }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 1.5),
  marginBottom: theme.spacing(0.5),
  borderRadius: 12,
  background: alpha($color, $isDark ? 0.15 : 0.1),
  border: `1px solid ${alpha($color, 0.3)}`,
  cursor: 'pointer',
  transition: transitionPresets.button,
  '&:hover': {
    background: alpha($color, $isDark ? 0.2 : 0.15),
  },
}));

const PeriodBadge = styled(Chip, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $color: string }>(({ $color }) => ({
  backgroundColor: alpha($color, 0.2),
  color: $color,
  fontWeight: 600,
  fontSize: '0.75rem',
  height: 26,
  '& .MuiChip-icon': {
    color: $color,
  },
}));

interface TimelineContainerProps {
  $isDark: boolean;
}

const TimelineContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<TimelineContainerProps>(({ theme }) => ({
  position: 'relative',
  paddingLeft: theme.spacing(3),
  marginLeft: theme.spacing(1),
}));

const ThreadLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 11,
  top: 0,
  bottom: 0,
  width: 2,
  background: `linear-gradient(180deg, ${theme.palette.divider} 0%, ${alpha(theme.palette.divider, 0.3)} 100%)`,
  borderRadius: 1,
}));

interface TimelineItemProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $actionColor: string;
}

const TimelineItem = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<TimelineItemProps>(({ theme, $isDark, $neonAccent, $actionColor }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    borderRadius: 10,
    background: glass.background,
    border: `1px solid ${neonStyles ? neonStyles.border : alpha($actionColor, 0.2)}`,
    position: 'relative',
    transition: transitionPresets.button,
    cursor: 'pointer',
    '&:hover': {
      background: glass.backgroundHover,
      borderColor: alpha($actionColor, 0.4),
      transform: 'translateX(4px)',
    },
    // Thread connector dot
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -20,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: $actionColor,
      border: `2px solid ${$isDark ? '#1a1a2e' : '#ffffff'}`,
      boxShadow: `0 0 0 2px ${alpha($actionColor, 0.3)}`,
    },
  };
});

const ActionIconBox = styled(
  Box,
  transientOptions,
)<{ $color: string }>(({ $color }) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha($color, 0.15),
  color: $color,
  flexShrink: 0,
}));

interface EmptyStateProps {
  $isDark: boolean;
}

const EmptyState = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<EmptyStateProps>(({ theme, $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(5),
    borderRadius: 16,
    background: glass.background,
    border: `1px solid ${glass.border}`,
    textAlign: 'center',
  };
});

interface LoadMoreButtonProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const LoadMoreButton = styled(motion.button, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<LoadMoreButtonProps>(({ theme, $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '10px 16px',
    marginTop: 12,
    borderRadius: 10,
    border: `1px solid ${neonStyles ? neonStyles.border : glass.border}`,
    background: neonStyles ? neonStyles.background : glass.background,
    color: neonStyles ? neonStyles.base : theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: transitionPresets.button,
    fontFamily: theme.typography.fontFamily,
    '&:hover': {
      background: glass.backgroundHover,
      borderColor: glass.borderHover,
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };
});

// ============================================================================
// Component
// ============================================================================

// ============================================================================
// ActivityDetailPanel — full audit context shown when a row is expanded.
// ============================================================================

/**
 * Parse a User-Agent string into a short "Browser X on OS" label.
 * Lightweight regex parsing — good enough for the audit detail panel
 * without taking on a UA-parser dependency. Falls back to the raw UA
 * string when the pattern doesn't match.
 */
function parseUserAgentLabel(ua: string): string {
  const browserMatch = ua.match(/(Edg|Chrome|Safari|Firefox|Opera)\/(\d+)/i);
  const osMatch = ua.match(/Windows NT [\d.]+|Mac OS X [\d_.]+|iPhone OS [\d_]+|Android [\d.]+|Linux/i);
  if (!browserMatch && !osMatch) return ua.length > 80 ? `${ua.slice(0, 80)}…` : ua;
  const browser = browserMatch ? browserMatch[1].replace('Edg', 'Edge') : 'Unknown browser';
  const os = osMatch ? osMatch[0].replace(/_/g, '.').replace('Mac OS X', 'macOS') : 'Unknown OS';
  return `${browser} on ${os}`;
}

interface ActivityDetailPanelProps {
  event: ActivityEvent;
  isDark: boolean;
}

function ActivityDetailPanel({ event, isDark }: ActivityDetailPanelProps) {
  const { t } = useI18n();
  const detailBg = isDark ? alpha(baseColors.neutral[800], 0.4) : alpha(baseColors.neutral[100], 0.6);
  const dividerColor = isDark ? alpha(baseColors.neutral[600], 0.3) : alpha(baseColors.neutral[300], 0.6);

  const rows: Array<{ label: string; value: React.ReactNode }> = [];
  rows.push({ label: t('entityActivityTimeline.action'), value: event.actionName ?? event.action });
  rows.push({
    label: t('entityActivityTimeline.resource'),
    value: event.type + (event.resourceId ? ` · ${event.resourceId}` : ''),
  });
  rows.push({ label: t('entityActivityTimeline.when'), value: formatFullDateTime(event.createdAt) });

  if (event.actorName || event.actorEmail) {
    rows.push({
      label: t('entityActivityTimeline.actor'),
      value: [event.actorName, event.actorEmail, event.actorRole].filter(Boolean).join(' · '),
    });
  }
  if (event.ipAddress) rows.push({ label: t('entityActivityTimeline.ipAddress'), value: event.ipAddress });
  if (event.userAgent) {
    rows.push({
      label: t('entityActivityTimeline.device'),
      value: (
        <GlassTooltip title={event.userAgent}>
          <span>{parseUserAgentLabel(event.userAgent)}</span>
        </GlassTooltip>
      ),
    });
  }
  if (event.method || event.path) {
    rows.push({
      label: t('entityActivityTimeline.request'),
      value: [event.method, event.path].filter(Boolean).join(' '),
    });
  }
  if (event.status) rows.push({ label: t('entityActivityTimeline.status'), value: event.status });
  if (event.severity && event.severity !== 'info')
    rows.push({ label: t('entityActivityTimeline.severity'), value: event.severity });
  if (typeof event.duration === 'number')
    rows.push({ label: t('entityActivityTimeline.duration'), value: `${event.duration} ms` });
  if (event.tags && event.tags.length > 0) {
    rows.push({
      label: t('entityActivityTimeline.tags'),
      value: (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {event.tags.map((tag) => (
            <Chip key={tag} size="small" label={tag} variant="outlined" />
          ))}
        </Box>
      ),
    });
  }
  if (event.errorMessage) rows.push({ label: t('entityActivityTimeline.error'), value: event.errorMessage });

  return (
    <Box
      sx={{
        ml: 4,
        mt: 0.5,
        mb: 1,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        bgcolor: detailBg,
        borderLeft: `2px solid ${dividerColor}`,
        display: 'grid',
        gridTemplateColumns: 'minmax(80px, max-content) 1fr',
        columnGap: 1.5,
        rowGap: 0.5,
      }}
    >
      {rows.map(({ label, value }) => (
        <Box key={label} sx={{ display: 'contents' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.primary', wordBreak: 'break-word' }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export function EntityActivityTimeline({
  events,
  loading = false,
  error,
  maxItems,
  groupByTimeOfDay = true,
  showThreading = true,
  onLoadMore,
  hasMore = false,
  onEventClick,
  expandableRows = true,
  resourceLabelOverride,
  showAvatars = true,
  relativeTime = true,
  variant = 'glass',
  neonAccent,
  emptyMessage,
  title,
  showCard = true,
  defaultCollapsed = false,
}: EntityActivityTimelineProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();
  const [collapsedPeriods, setCollapsedPeriods] = useState<Set<string>>(new Set());
  // Track which audit-row is expanded. Single string (not Set) so only one
  // detail panel is open at a time — fewer cognitive surfaces and avoids the
  // page becoming a wall of expanded panels for active loans.
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

  // Apply defaultCollapsed on mount when data is available
  useEffect(() => {
    if (defaultCollapsed && groupByTimeOfDay && events.length > 0) {
      const grouped = groupEventsByDateAndTime(events, t);
      const allPeriodKeys = new Set<string>();
      for (const dayGroup of grouped) {
        for (const period of dayGroup.periods) {
          allPeriodKeys.add(`${dayGroup.dateKey}-${period.period}`);
        }
      }
      setCollapsedPeriods(allPeriodKeys);
    }
  }, [defaultCollapsed, groupByTimeOfDay, events]);

  // Apply maxItems limit
  const displayedEvents = useMemo(() => {
    if (maxItems && maxItems > 0) {
      return events.slice(0, maxItems);
    }
    return events;
  }, [events, maxItems]);

  // Group events by date and time-of-day
  const groupedData = useMemo(() => {
    if (!groupByTimeOfDay) {
      // Return flat structure when grouping is disabled
      return displayedEvents;
    }
    return groupEventsByDateAndTime(displayedEvents, t);
  }, [displayedEvents, groupByTimeOfDay, t]);

  // Handle period collapse toggle
  const togglePeriod = useCallback((periodKey: string) => {
    setCollapsedPeriods((prev) => {
      const next = new Set(prev);
      if (next.has(periodKey)) {
        next.delete(periodKey);
      } else {
        next.add(periodKey);
      }
      return next;
    });
  }, []);

  // Apply the optional resource label override to a description string.
  // Audit-service events read "<actor> verb this <resource>"; the loan
  // detail page wants "<actor> verb this loan" instead of "this case".
  const applyResourceOverride = useCallback(
    (description: string): string => {
      if (!resourceLabelOverride) return description;
      // Replace the first instance of "this <word>" or "the <word> list" so
      // we don't accidentally rewrite descriptions that happen to mention
      // the original resource word elsewhere (e.g., comments).
      return description
        .replace(/\bthis\s+\w+/i, `this ${resourceLabelOverride}`)
        .replace(/\bthe\s+\w+\s+list\b/i, `the ${resourceLabelOverride} list`);
    },
    [resourceLabelOverride],
  );

  // Render single timeline item with optional click-to-expand audit panel.
  const renderTimelineItem = (event: ActivityEvent) => {
    const IconComponent = getActionIcon(event.action);
    const actionColor = getActionColor(event.action);
    const isExpanded = expandableRows && expandedEventId === event.id;
    const description = applyResourceOverride(event.description);

    const handleRowClick = () => {
      if (expandableRows) {
        setExpandedEventId((prev) => (prev === event.id ? null : event.id));
      }
      onEventClick?.(event);
    };

    return (
      <Box key={event.id} sx={{ display: 'flex', flexDirection: 'column' }}>
        <TimelineItem
          $isDark={isDark}
          $neonAccent={neonAccent}
          $actionColor={actionColor}
          onClick={handleRowClick}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 10 }}
          whileHover={{ scale: 1.01 }}
          sx={{ cursor: expandableRows || onEventClick ? 'pointer' : 'default' }}
        >
          {/* Action Icon */}
          <ActionIconBox $color={actionColor}>
            <IconComponent fontSize="small" />
          </ActionIconBox>
          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: 500,
                  flex: 1,
                }}
              >
                {description}
              </Typography>
              <GlassTooltip title={formatFullDateTime(event.createdAt)}>
                <span>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {relativeTime ? formatCompactRelativeTime(event.createdAt) : formatTime(event.createdAt)}
                  </Typography>
                </span>
              </GlassTooltip>
              {expandableRows && (
                <IconButton
                  size="small"
                  sx={{ color: 'text.secondary', p: 0 }}
                  aria-label={
                    isExpanded ? t('entityActivityTimeline.collapseDetails') : t('entityActivityTimeline.expandDetails')
                  }
                  tabIndex={-1}
                >
                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              )}
            </Box>

            {/* Actor info */}
            {event.actorName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {showAvatars && (
                  // 18px is below `small` (20px) — timeline rows pack two
                  // avatars + caption on one line, so the size override stays.
                  <UserAvatar
                    name={event.actorName}
                    src={event.actorAvatar}
                    size="small"
                    sx={{ width: 18, height: 18, fontSize: '0.6875rem' }}
                  />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {event.actorName}
                  {event.actorRole ? ` · ${event.actorRole}` : ''}
                </Typography>
              </Box>
            )}
          </Box>
        </TimelineItem>

        {/* Expandable audit detail panel — surfaces every forensic field
            the audit service captured: full timestamp, IP, parsed UA, HTTP
            method/path, severity, status, tags, duration, error. */}
        {expandableRows && (
          <Collapse in={isExpanded} unmountOnExit>
            <ActivityDetailPanel event={event} isDark={isDark} />
          </Collapse>
        )}
      </Box>
    );
  };

  // Render grouped timeline
  const renderGroupedTimeline = (data: GroupedTimelineData[]) => {
    return (
      <AnimatePresence mode="popLayout">
        {data.map((dayGroup) => (
          <Box key={dayGroup.dateKey}>
            {/* Date divider */}
            <DateDivider>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              >
                {dayGroup.dateLabel}
              </Typography>
            </DateDivider>

            {/* Time periods */}
            {dayGroup.periods.map((period) => {
              const periodKey = `${dayGroup.dateKey}-${period.period}`;
              const periodInfo = TIME_PERIOD_KEYS[period.period];
              const PeriodIcon = periodInfo.icon;
              const isCollapsed = collapsedPeriods.has(periodKey);
              const eventCount = period.events.length;

              return (
                <Box key={periodKey} sx={{ mb: 2 }}>
                  {/* Period header */}
                  <PeriodHeader $color={periodInfo.color} $isDark={isDark} onClick={() => togglePeriod(periodKey)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeriodBadge
                        $color={periodInfo.color}
                        icon={<PeriodIcon fontSize="small" />}
                        label={t(period.label)}
                        size="small"
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {eventCount} {eventCount === 1 ? t('timeline.activity') : t('timeline.activities')}
                      </Typography>
                    </Box>
                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                      {isCollapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                    </IconButton>
                  </PeriodHeader>
                  {/* Period events */}
                  <Collapse in={!isCollapsed}>
                    <TimelineContainer $isDark={isDark}>
                      {showThreading && <ThreadLine />}
                      {period.events.map(renderTimelineItem)}
                    </TimelineContainer>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        ))}
      </AnimatePresence>
    );
  };

  // Render flat timeline (no grouping)
  const renderFlatTimeline = (evts: ActivityEvent[]) => {
    return (
      <TimelineContainer $isDark={isDark}>
        {showThreading && <ThreadLine />}
        <AnimatePresence mode="popLayout">{evts.map(renderTimelineItem)}</AnimatePresence>
      </TimelineContainer>
    );
  };

  // Render content
  const renderContent = () => {
    // Error state
    if (error) {
      return (
        <EmptyState $isDark={isDark}>
          <ErrorIcon sx={{ fontSize: iconSizes['3xl'], color: 'error.main', mb: 2 }} />
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </EmptyState>
      );
    }

    // Loading state
    if (loading && displayedEvents.length === 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ display: 'flex', gap: 2, px: 3 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="40%" />
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    // Empty state
    if (displayedEvents.length === 0) {
      return (
        <EmptyState $isDark={isDark}>
          <InfoIcon sx={{ fontSize: iconSizes['3xl'], color: 'text.disabled', mb: 2 }} />
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
            }}
          >
            {emptyMessage || t('timeline.noActivity')}
          </Typography>
        </EmptyState>
      );
    }

    // Render timeline
    return (
      <>
        {groupByTimeOfDay
          ? renderGroupedTimeline(groupedData as GroupedTimelineData[])
          : renderFlatTimeline(groupedData as ActivityEvent[])}
        {/* Load More */}
        {hasMore && onLoadMore && (
          <LoadMoreButton
            $isDark={isDark}
            $neonAccent={neonAccent}
            onClick={onLoadMore}
            disabled={loading}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
                {t('common.loading')}
              </>
            ) : (
              t('timeline.loadMore')
            )}
          </LoadMoreButton>
        )}
        {/* Show count */}
        {maxItems && events.length > maxItems && (
          <Box sx={{ textAlign: 'center', mt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('timeline.showingCount', { shown: maxItems, total: events.length })}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  // Main component
  const timeline = (
    <Container>
      {/* Header */}
      {title && (
        <HeaderBox>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
            }}
          >
            {title}
          </Typography>
        </HeaderBox>
      )}

      {/* Content */}
      {renderContent()}
    </Container>
  );

  // Wrap in card if needed
  if (showCard && variant === 'glass') {
    return <GlassCard neonAccent={neonAccent}>{timeline}</GlassCard>;
  }

  return timeline;
}

EntityActivityTimeline.displayName = 'EntityActivityTimeline';

export default EntityActivityTimeline;
